import React, { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import PageHeader from '../shared/PageHeader';
import { DataTable } from '../../components/Tables';
import ConfirmDeleteModal from '../../components/common/ConfirmDeleteModal';
import { adminUsersApi, extractList, rolesApi } from '../../api/facades';
import { useAuth } from '../../context/AuthContext';

const UsersListPage: React.FC = () => {
  const { user: authUser } = useAuth();
  const queryClient = useQueryClient();
  const [deleteId, setDeleteId] = React.useState<number | null>(null);
  const [isCreateOpen, setIsCreateOpen] = React.useState(false);
  const [isEditOpen, setIsEditOpen] = React.useState(false);
  const [editingUser, setEditingUser] = React.useState<any | null>(null);
  const [formEmail, setFormEmail] = React.useState('');
  const [formPassword, setFormPassword] = React.useState('');
  const [formFirstName, setFormFirstName] = React.useState('');
  const [formLastName, setFormLastName] = React.useState('');
  const [formMobile, setFormMobile] = React.useState('');
  const [formRoleIds, setFormRoleIds] = React.useState<number[]>([]);
  const [formIsActive, setFormIsActive] = React.useState<0 | 1>(1);

  const { data: res, isLoading } = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => adminUsersApi.list({ per_page: 100 }),
  });
  const rows = extractList(res);

  const { data: rolesRes } = useQuery({
    queryKey: ['roles'],
    queryFn: () => rolesApi.list(),
  });
  const roles = extractList(rolesRes);
  const isSuperAdminUser = useMemo(
    () =>
      Array.isArray((authUser as any)?.roles) &&
      (authUser as any).roles.some((r: any) => r?.slug === 'super_admin'),
    [authUser],
  );
  const assignableRoles = useMemo(
    () =>
      isSuperAdminUser ? roles : roles.filter((r: any) => r?.slug !== 'super_admin'),
    [roles, isSuperAdminUser],
  );

  const deleteMu = useMutation({
    mutationFn: (id: number) => adminUsersApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setDeleteId(null);
      toast.success('User deleted.');
    },
    onError: (e: any) => toast.error(e?.message || 'Delete failed.'),
  });

  const createMu = useMutation({
    mutationFn: () =>
      adminUsersApi.create({
        email: formEmail,
        password: formPassword || undefined,
        first_name: formFirstName || undefined,
        last_name: formLastName || undefined,
        mobile: formMobile || undefined,
        role_ids: formRoleIds,
        is_active: formIsActive,
      }),
    onSuccess: () => {
      toast.success('User created.');
      setIsCreateOpen(false);
      setFormEmail('');
      setFormPassword('');
      setFormFirstName('');
      setFormLastName('');
      setFormMobile('');
      setFormRoleIds([]);
      setFormIsActive(1);
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
    onError: (e: any) => {
      toast.error(e?.body?.message || e?.message || 'Create failed.');
    },
  });

  const updateMu = useMutation({
    mutationFn: () =>
      adminUsersApi.update(editingUser.id, {
        email: formEmail,
        password: formPassword || undefined,
        first_name: formFirstName || undefined,
        last_name: formLastName || undefined,
        mobile: formMobile || undefined,
        role_ids: formRoleIds,
        is_active: formIsActive,
      }),
    onSuccess: () => {
      toast.success('User updated.');
      setIsEditOpen(false);
      setEditingUser(null);
      setFormPassword('');
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
    onError: (e: any) => {
      toast.error(e?.body?.message || e?.message || 'Update failed.');
    },
  });

  const openCreate = () => {
    setEditingUser(null);
    setFormEmail('');
    setFormPassword('');
    setFormFirstName('');
    setFormLastName('');
    setFormMobile('');
    setFormRoleIds([]);
    setFormIsActive(1);
    setIsCreateOpen(true);
  };

  const openEdit = (user: any) => {
    setEditingUser(user);
    setFormEmail(user.email ?? '');
    setFormPassword('');
    setFormFirstName(user.people?.first_name ?? '');
    setFormLastName(user.people?.last_name ?? '');
    setFormMobile(user.people?.mobile ?? '');
    const userRoleIds =
      Array.isArray(user.roles) && user.roles.length
        ? user.roles.map((r: any) => r.id).filter((id: any) => id != null)
        : [];
    const allowedIds = new Set(assignableRoles.map((r: any) => r.id));
    setFormRoleIds(
      isSuperAdminUser ? userRoleIds : userRoleIds.filter((id: number) => allowedIds.has(id)),
    );
    setFormIsActive(user.is_active === 'Yes' || user.is_active === 1 ? 1 : 0);
    setIsEditOpen(true);
  };

  const columns = [
    { key: 'email', label: 'Email' },
    {
      key: 'roles',
      label: 'Roles',
      render: (r: any) =>
        Array.isArray(r.roles) && r.roles.length
          ? r.roles.map((role: any) => role.slug || role.description || role.name).join(', ')
          : '—',
    },
    {
      key: 'is_active',
      label: 'Active',
      render: (r: any) => (r.is_active === 'Yes' || r.is_active === 1 ? 'Yes' : 'No'),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (r: any) => (
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="text-xs text-primary-600 hover:text-primary-700"
            onClick={() => openEdit(r)}
          >
            Edit
          </button>
          <button
            type="button"
            className="text-xs text-primary-600 hover:text-primary-700"
            onClick={async () => {
              try {
                await adminUsersApi.sendResetLink(r.id);
                toast.success('Password reset email sent.');
              } catch (e: any) {
                toast.error(e?.body?.message || e?.message || 'Failed to send reset email.');
              }
            }}
          >
            Reset password
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Users"
        subtitle="Manage user accounts and access."
        primaryAction={{ label: 'Add user', onClick: openCreate }}
      />
      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6">
        <DataTable
          columns={columns}
          data={rows}
          loading={isLoading}
          emptyMessage="No users."
          onEdit={(id) => {
            const user = rows.find((u: any) => u.id === id);
            if (user) openEdit(user);
          }}
          onDelete={(id) => setDeleteId(Number(id))}
        />
      </div>
      <ConfirmDeleteModal
        open={deleteId != null}
        onClose={() => setDeleteId(null)}
        onConfirm={() => deleteId != null && deleteMu.mutate(deleteId)}
        title="Delete user"
        message="Are you sure? This cannot be undone."
        loading={deleteMu.isPending}
      />

      {(isCreateOpen || isEditOpen) && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl w-full max-w-lg mx-4">
            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                {isCreateOpen ? 'Add user' : 'Edit user'}
              </h2>
              <button
                type="button"
                onClick={() => {
                  setIsCreateOpen(false);
                  setIsEditOpen(false);
                  setEditingUser(null);
                }}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
              >
                <i className="fas fa-times text-sm" />
              </button>
            </div>
            <form
              className="px-6 py-5 space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                if (!formEmail) {
                  toast.error('Email is required.');
                  return;
                }
                if (isCreateOpen) {
                  createMu.mutate();
                } else if (isEditOpen && editingUser) {
                  updateMu.mutate();
                }
              }}
            >
              <div>
                <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">
                  First name
                </label>
                <input
                  type="text"
                  value={formFirstName}
                  onChange={(e) => setFormFirstName(e.target.value)}
                  className="w-full rounded-lg bg-gray-50 py-2 px-3 text-sm text-slate-700 focus:outline-none dark:bg-slate-900 dark:text-slate-200 border border-slate-200 dark:border-slate-700"
                  placeholder="e.g. Madalitso"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">
                  Last name
                </label>
                <input
                  type="text"
                  value={formLastName}
                  onChange={(e) => setFormLastName(e.target.value)}
                  className="w-full rounded-lg bg-gray-50 py-2 px-3 text-sm text-slate-700 focus:outline-none dark:bg-slate-900 dark:text-slate-200 border border-slate-200 dark:border-slate-700"
                  placeholder="Surname"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">
                  Mobile
                </label>
                <input
                  type="text"
                  value={formMobile}
                  onChange={(e) => setFormMobile(e.target.value)}
                  className="w-full rounded-lg bg-gray-50 py-2 px-3 text-sm text-slate-700 focus:outline-none dark:bg-slate-900 dark:text-slate-200 border border-slate-200 dark:border-slate-700"
                  placeholder="+265..."
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={formEmail}
                  onChange={(e) => setFormEmail(e.target.value)}
                  className="w-full rounded-lg bg-gray-50 py-2 px-3 text-sm text-slate-700 focus:outline-none dark:bg-slate-900 dark:text-slate-200 border border-slate-200 dark:border-slate-700"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">
                  Password
                </label>
                <input
                  type="password"
                  value={formPassword}
                  onChange={(e) => setFormPassword(e.target.value)}
                  className="w-full rounded-lg bg-gray-50 py-2 px-3 text-sm text-slate-700 focus:outline-none dark:bg-slate-900 dark:text-slate-200 border border-slate-200 dark:border-slate-700"
                  placeholder={isEditOpen ? 'Leave blank to keep current password' : 'Optional (auto-generated if blank)'}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">
                  Roles
                </label>
                <div className="flex flex-wrap gap-2">
                  {assignableRoles.map((role: any) => (
                    <label
                      key={role.id}
                      className="inline-flex items-center gap-1 text-xs text-slate-600 dark:text-slate-300"
                    >
                      <input
                        type="checkbox"
                        className="rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                        checked={formRoleIds.includes(role.id)}
                        onChange={(e) => {
                          setFormRoleIds((prev) =>
                            e.target.checked
                              ? [...prev, role.id]
                              : prev.filter((id) => id !== role.id),
                          );
                        }}
                      />
                      <span>{role.slug || role.description || role.name}</span>
                    </label>
                  ))}
                  {assignableRoles.length === 0 && (
                    <p className="text-xs text-slate-500">
                      No roles found. Configure roles first.
                    </p>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">
                  Status
                </label>
                <select
                  value={formIsActive}
                  onChange={(e) =>
                    setFormIsActive(Number(e.target.value) === 1 ? 1 : 0)
                  }
                  className="w-full rounded-lg bg-gray-50 py-2 px-3 text-sm text-slate-700 focus:outline-none dark:bg-slate-900 dark:text-slate-200 border border-slate-200 dark:border-slate-700"
                >
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setIsCreateOpen(false);
                    setIsEditOpen(false);
                    setEditingUser(null);
                  }}
                  className="px-3 py-1.5 text-xs rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMu.isPending || updateMu.isPending}
                  className="px-3 py-1.5 text-xs rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60"
                >
                  {isCreateOpen
                    ? createMu.isPending
                      ? 'Creating...'
                      : 'Create user'
                    : updateMu.isPending
                    ? 'Saving...'
                    : 'Save changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersListPage;

